// Agents vocaux — /voice-agents. The AI agents that answer the phone, a
// product of their own (not a tab of a text agent): each has its greeting,
// phone instructions, voice, tools and number. Created from a template,
// opened on their own page.
// Zones: header (scope in numbers, « Nouvel agent vocal ») → « À vérifier »
// (numbers no agent answers, live agents not ready, lines silent for days)
// → state tiles → the agents (rows) or the first-agent steps.
// API: GET /api/voice-agents (agents, numbers, unrouted_numbers); POST
// /api/voice-agents (create, then open its page).

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, CircleCheck, Headphones, Phone, PhoneCall, Plus } from 'lucide-react';
import {
    Badge, Banner, Card, Field, IconChip, Kpi, Modal, PageHeader, Steps,
} from '../../design/ui';
import { TEMPLATES, canManage, createAgent, fail, formatDay, listAgents } from './api';
import { formatRelative } from '../../utils/voiceFormat';
import { agentGaps, agentsAttention, agentsFigures } from './voiceModel';
import { AttentionCard, ChoiceTiles, formatCount, pluralT } from './VoiceKit';
import { numberLabel } from './phoneFormat';
import './VoiceSetup.css';

const TEMPLATE_DEFAULTS = {
    parcel_support: ['Service client colis', 'Suit un colis à partir de son numéro, ouvre une demande, raccroche.'],
    reception: ['Accueil et prise de message', 'Répond aux questions simples, note le motif et ouvre une demande pour l’équipe.'],
    blank: ['Vierge', 'Aucune consigne : vous écrivez tout.'],
};

const GAP_LABELS = {
    greeting: ['voiceSetup.agents.noGreeting', 'pas d’accueil'],
    instructions: ['voiceSetup.agents.noInstructions', 'pas de consignes'],
    tools: ['voiceSetup.agents.noTools', 'aucun outil'],
};

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const CreateAgentModal = ({ onClose, t }) => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [template, setTemplate] = useState('parcel_support');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || busy) return;
        setBusy(true);
        setError('');
        try {
            const res = await createAgent({ name: name.trim(), template });
            navigate(`/voice-agents/${res.data.id}`);
        } catch (err) {
            setError(fail(err, t('voiceSetup.agents.createError', 'L’agent vocal n’a pas pu être créé.')));
            setBusy(false);
        }
    };

    return (
        <Modal
            wide
            icon={Headphones}
            title={t('voiceSetup.agents.createTitle', 'Nouvel agent vocal')}
            description={t('voiceUi.agents.createDesc', 'Partez d’un modèle : vous ajustez ensuite l’accueil, les consignes, la voix et les outils sur sa page.')}
            onClose={onClose}
            closeLabel={t('voiceUi.close', 'Fermer')}
            footer={(
                <>
                    <button type="button" className="u-btn u-btn--secondary" onClick={onClose} disabled={busy}>
                        {t('voiceSetup.cancel', 'Annuler')}
                    </button>
                    <button type="submit" form="voice-agent-create" className="u-btn u-btn--primary" disabled={!name.trim() || busy}>
                        {busy ? t('voiceSetup.agents.creating', 'Création…') : t('voiceSetup.agents.create', 'Créer l’agent')}
                    </button>
                </>
            )}
        >
            <form id="voice-agent-create" className="u-form" onSubmit={submit}>
                {error && <Banner tone="danger">{error}</Banner>}
                <Field
                    label={t('voiceSetup.agents.name', 'Nom')}
                    htmlFor="voice-agent-name"
                    help={t('voiceUi.agents.nameHelp', 'Le nom que votre équipe voit dans Unanimity. L’appelant ne l’entend pas.')}
                >
                    <input
                        id="voice-agent-name"
                        className="u-input"
                        value={name}
                        maxLength={80}
                        autoFocus
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('voiceSetup.agents.namePlaceholder', 'Ex. : Service client')}
                    />
                </Field>
                <div className="u-field">
                    <span className="u-label">{t('voiceSetup.agents.template', 'Point de départ')}</span>
                    <ChoiceTiles
                        label={t('voiceSetup.agents.template', 'Point de départ')}
                        value={template}
                        onChange={setTemplate}
                        options={TEMPLATES.map((key) => ({
                            value: key,
                            title: t(`voiceSetup.agents.templates.${key}.label`, TEMPLATE_DEFAULTS[key][0]),
                            hint: t(`voiceSetup.agents.templates.${key}.hint`, TEMPLATE_DEFAULTS[key][1]),
                        }))}
                    />
                </div>
            </form>
        </Modal>
    );
};

const AgentRow = ({ agent, t, lang }) => {
    const tp = pluralT(t);
    const live = agent.numbers.length > 0;
    const facts = [
        agent.voice ? capitalize(agent.voice) : t('voiceSetup.agents.defaultVoice', 'Voix par défaut'),
        agent.language ? t(`voiceSetup.agent.languages.${agent.language}`, agent.language) : t('voiceSetup.agents.anyLanguage', 'Langue de l’appelant'),
        tp('voiceUi.agents.toolCount', '{{count}} outil', '{{count}} outils', { count: agent.voice_tools }),
    ];
    const gaps = agentGaps(agent).map((gap) => t(GAP_LABELS[gap][0], GAP_LABELS[gap][1]));
    return (
        <li>
            <Link to={`/voice-agents/${agent.id}`} className="u-row voice-v2__link-row">
                <IconChip icon={Headphones} tone={live ? undefined : 'neutral'} />
                <div className="u-row__main">
                    <div className="u-row__title">
                        <span>{agent.name || t('voiceSetup.unnamedAgent', 'Agent sans nom')}</span>
                        {live
                            ? <Badge tone="success" dot>{t('voiceSetup.agents.live', 'En ligne')}</Badge>
                            : <Badge dot>{t('voiceSetup.agents.noNumber', 'Sans numéro')}</Badge>}
                    </div>
                    <div className="u-row__meta voice-v2__facts">
                        <span className={live ? 'voice-v2__fact-strong' : undefined}>
                            {live
                                ? t('voiceSetup.agent.answers', 'Répond au {{numbers}}', { numbers: agent.numbers.map(numberLabel).join(', ') })
                                : t('voiceSetup.agents.noNumberHint', 'Ne reçoit pas d’appels : donnez-lui un numéro.')}
                        </span>
                        {facts.map((fact) => <span key={fact}>{fact}</span>)}
                    </div>
                    {gaps.length > 0 && (
                        <div className="u-row__meta u-row__meta--wrap voice-v2__gap">
                            {t('voiceSetup.agents.gaps', 'À compléter : {{gaps}}', { gaps: gaps.join(', ') })}
                        </div>
                    )}
                </div>
                <div className="u-row__side">
                    <span className="voice-v2__figure">
                        <span className="u-num voice-v2__figure-value">{formatCount(agent.calls_30d, lang)}</span>
                        <span className="voice-v2__figure-label">
                            {agent.last_call_at
                                ? tp('voiceUi.agents.callsLast', 'appel · 30 j · dernier le {{date}}', 'appels · 30 j · dernier le {{date}}', { count: agent.calls_30d, date: formatDay(agent.last_call_at, lang) })
                                : tp('voiceUi.agents.callsNone', 'appel · 30 j', 'appels · 30 j', { count: agent.calls_30d })}
                        </span>
                    </span>
                    <ChevronRight className="voice-v2__chevron" aria-hidden="true" />
                </div>
            </Link>
        </li>
    );
};

const VoiceAgents = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n?.language || 'fr';
    const tp = pluralT(t);
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const manage = canManage();

    useEffect(() => {
        listAgents()
            .then((res) => setData(res.data))
            .catch((err) => setError(fail(err, t('voiceSetup.agents.loadError', 'Les agents vocaux n’ont pas pu être chargés.'))));
    }, [t]);

    const agents = data?.agents || [];
    const figures = agentsFigures(data);

    const attention = agentsAttention(data).map((item) => {
        if (item.kind === 'unrouted') {
            return {
                ...item,
                title: tp('voiceUi.attention.unrouted', '{{count}} numéro ne renvoie vers aucun agent', '{{count}} numéros ne renvoient vers aucun agent', { count: item.count }),
                detail: t('voiceUi.attention.unroutedDetail', 'Les appels n’y sont pas décrochés.'),
                action: manage ? { label: t('voiceSetup.agents.routeNumbers', 'Choisir l’agent qui répond'), onClick: () => navigate('/voice-numbers') } : null,
            };
        }
        if (item.kind === 'silent') {
            return {
                ...item,
                title: t('voiceUi.attention.agentSilent', 'Aucun appel pour {{name}} depuis le {{date}}', { name: item.agent.name, date: formatDay(item.last, lang) }),
                detail: tp(
                    'voiceUi.attention.silentDetail',
                    '{{count}} jour sans appel sur le {{number}} : appelez-le pour vérifier que l’agent décroche.',
                    '{{count}} jours sans appel sur le {{number}} : appelez-le pour vérifier que l’agent décroche.',
                    { count: item.days, number: item.agent.numbers.map(numberLabel).join(', ') },
                ),
                action: { label: t('voiceUi.attention.openAgent', 'Ouvrir l’agent'), onClick: () => navigate(`/voice-agents/${item.agent.id}`) },
            };
        }
        return {
            ...item,
            title: t('voiceUi.attention.agentNotReady', '{{name}} répond au téléphone sans être prêt', { name: item.agent.name }),
            detail: t('voiceUi.attention.gapsDetail', 'Manque : {{gaps}}.', { gaps: item.gaps.map((gap) => t(GAP_LABELS[gap][0], GAP_LABELS[gap][1])).join(', ') }),
            action: { label: t('voiceUi.attention.complete', 'Compléter'), onClick: () => navigate(`/voice-agents/${item.agent.id}`) },
        };
    });

    let subtitle = t('voiceUi.agents.subtitleLoading', 'Les agents IA qui décrochent le téléphone.');
    if (data && agents.length) {
        subtitle = [
            tp('voiceUi.agents.scopeAgents', '{{count}} agent vocal', '{{count}} agents vocaux', { count: figures.total }),
            tp('voiceUi.agents.scopeLive', '{{count}} en ligne', '{{count}} en ligne', { count: figures.live }),
            tp('voiceUi.agents.scopeCalls', '{{count}} appel sur 30 jours', '{{count}} appels sur 30 jours', { count: figures.calls30d }),
        ].join(' · ');
    } else if (data) {
        subtitle = t('voiceUi.agents.scopeEmpty', 'Aucun agent vocal pour l’instant : créez-en un, donnez-lui un numéro.');
    }

    const renderKpis = () => {
        if (!data) {
            if (error) return null;
            return (
                <div className="u-kpi-row" aria-hidden="true">
                    {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton voice-v2__kpi-skeleton" />)}
                </div>
            );
        }
        if (!agents.length) return null;
        const allReady = figures.ready === figures.total;
        return (
            <div className="u-kpi-row">
                <Kpi
                    icon={Headphones}
                    label={t('voiceUi.agents.kpiAgents', 'Agents vocaux')}
                    value={formatCount(figures.total, lang)}
                    context={t('voiceUi.agents.kpiAgentsContext', '{{live}} en ligne · {{off}} sans numéro', { live: figures.live, off: figures.total - figures.live })}
                />
                <Kpi
                    icon={Phone}
                    tone={data.unrouted_numbers > 0 ? 'warning' : 'success'}
                    label={t('voiceUi.agents.kpiNumbers', 'Numéros')}
                    value={formatCount(data.numbers, lang)}
                    context={data.unrouted_numbers > 0
                        ? tp('voiceUi.agents.kpiUnrouted', '{{count}} sans agent', '{{count}} sans agent', { count: data.unrouted_numbers })
                        : t('voiceUi.agents.kpiAllRouted', 'chacun relié à un agent')}
                    contextTone={data.unrouted_numbers > 0 ? 'warning' : undefined}
                    onClick={manage ? () => navigate('/voice-numbers') : undefined}
                    title={manage ? t('voiceUi.openNumbers', 'Ouvrir les numéros') : undefined}
                />
                <Kpi
                    icon={PhoneCall}
                    tone="info"
                    label={t('voiceUi.agents.kpiCalls', 'Appels sur 30 jours')}
                    value={formatCount(figures.calls30d, lang)}
                    context={figures.lastCall
                        ? t('voiceUi.lastCall', 'dernier : {{when}}', { when: formatRelative(figures.lastCall) })
                        : t('voiceUi.noCallYet', 'aucun appel reçu')}
                    onClick={() => navigate('/voice-tickets')}
                    title={t('voiceUi.openCalls', 'Ouvrir les appels')}
                />
                <Kpi
                    icon={CircleCheck}
                    tone={allReady ? 'success' : 'warning'}
                    label={t('voiceUi.agents.kpiReady', 'Prêts à répondre')}
                    value={`${figures.ready} / ${figures.total}`}
                    context={allReady
                        ? t('voiceUi.agents.kpiReadyAll', 'accueil, consignes et outils renseignés')
                        : tp('voiceUi.agents.kpiReadyMissing', '{{count}} à compléter', '{{count}} à compléter', { count: figures.total - figures.ready })}
                    contextTone={allReady ? undefined : 'warning'}
                    meter={{ value: figures.ready, max: figures.total, tone: allReady ? 'success' : 'warning', label: t('voiceUi.agents.kpiReady', 'Prêts à répondre') }}
                />
            </div>
        );
    };

    const renderList = () => {
        if (!data) {
            if (error) return null;
            return (
                <section className="u-card voice-v2__list-skeleton" aria-busy="true">
                    {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
                </section>
            );
        }
        if (!agents.length) {
            return (
                <Card
                    icon={Headphones}
                    title={t('voiceSetup.agents.emptyTitle', 'Votre premier agent vocal')}
                    description={t('voiceSetup.agents.emptyText', 'Vos clients appellent un numéro, l’agent décroche 24 h/24 : il suit un colis, répond aux questions et ouvre une demande pour votre équipe.')}
                    footerNote={manage ? undefined : t('voiceUi.agents.adminOnly', 'Seuls les administrateurs du compte créent les agents vocaux.')}
                    footer={manage ? (
                        <button type="button" className="u-btn u-btn--primary" onClick={() => setCreating(true)}>
                            <Plus aria-hidden="true" />
                            {t('voiceSetup.agents.new', 'Nouvel agent vocal')}
                        </button>
                    ) : undefined}
                >
                    <Steps steps={[
                        {
                            key: 'create',
                            title: t('voiceSetup.agents.step1', 'Créez l’agent à partir d’un modèle'),
                            description: t('voiceUi.agents.step1Hint', 'Service client colis, accueil ou vierge : l’accueil, les consignes et les outils sont préremplis.'),
                            status: 'current',
                        },
                        {
                            key: 'number',
                            title: t('voiceSetup.agents.step2', 'Donnez-lui un numéro'),
                            description: t('voiceUi.agents.step2Hint', 'Un numéro par agent, choisi parmi ceux que propose Unanimity, sans engagement.'),
                            status: 'todo',
                        },
                        {
                            key: 'try',
                            title: t('voiceSetup.agents.step3', 'Essayez-le dans le navigateur, puis appelez-le'),
                            description: t('voiceUi.agents.step3Hint', 'L’essai ne crée rien : ni ticket, ni SMS.'),
                            status: 'todo',
                        },
                    ]}
                    />
                </Card>
            );
        }
        return (
            <Card
                icon={Headphones}
                title={t('voiceUi.agents.listTitle', 'Vos agents vocaux')}
                description={t('voiceUi.agents.listDesc', 'Indépendants de vos agents texte : chacun a son accueil, ses consignes à l’oral, sa voix, ses outils et son numéro.')}
                flush
            >
                <ul className="u-list">
                    {agents.map((agent) => <AgentRow key={agent.id} agent={agent} t={t} lang={lang} />)}
                </ul>
            </Card>
        );
    };

    return (
        <div className="u-page voice-v2">
            <PageHeader
                title={t('voiceSetup.agents.title', 'Agents vocaux')}
                subtitle={subtitle}
                actions={manage ? (
                    <>
                        <Link className="u-btn u-btn--secondary" to="/voice-numbers">
                            <Phone aria-hidden="true" />
                            {t('voiceUi.agents.numbersLink', 'Numéros')}
                        </Link>
                        {agents.length > 0 && (
                            <button type="button" className="u-btn u-btn--primary" onClick={() => setCreating(true)}>
                                <Plus aria-hidden="true" />
                                {t('voiceSetup.agents.new', 'Nouvel agent vocal')}
                            </button>
                        )}
                    </>
                ) : undefined}
            />
            <div className="u-well">
                {error && <Banner tone="danger">{error}</Banner>}
                <AttentionCard items={attention} t={t} />
                {renderKpis()}
                {renderList()}
            </div>
            {creating && <CreateAgentModal t={t} onClose={() => setCreating(false)} />}
        </div>
    );
};

export default VoiceAgents;
